/**
 * State type definitions
 * Defines GameState (persisted), UIState (ephemeral), and action types
 */

export type RoomVariant = "a" | "b";

import { EducationalMessage, Item, Message as GqlMessage, MessageResponse, Room } from "@/types/api";

export type Stats = {
    cash: number;
    investments: number;
    happiness: number;
};

export type GameOverReason = "none" | "cash" | "happiness" | "rent";

// -----------------------------------------------------------------------------
// Overlay Constants
// -----------------------------------------------------------------------------

export const OverlayType = {
    None: "none",
    Stats: "stats",
    Achievements: "achievements",
} as const;

export type OverlayType = (typeof OverlayType)[keyof typeof OverlayType];

// -----------------------------------------------------------------------------
// History Entry (for stats display)
// -----------------------------------------------------------------------------

export type HistoryEntry = {
    id: string;
    type: "cash" | "depot";
    change: number;
    newValue: number;
    timestamp: number;
    title: string;
};

// -----------------------------------------------------------------------------
// Toast Message Types
// -----------------------------------------------------------------------------

type ToastBaseType = {
    type: "cash" | "depot" | "happiness" | "earnings" | "achievement" | "item";
};

export type ToastStatChange = ToastBaseType & {
    type: "cash" | "depot" | "happiness" | "earnings";
    change: number;
};

export type ToastAchievement = ToastBaseType & {
    type: "achievement";
    achievement: NonNullable<MessageResponse["achievement"]>;
};

export type ToastItem = ToastBaseType & {
    type: "item";
    item: Item;
};

export type ToastMessage = ToastStatChange | ToastAchievement | ToastItem;

// -----------------------------------------------------------------------------
// Animation Sequencer Types
// -----------------------------------------------------------------------------

export type AnimationStep =
    | { type: "toasts"; toasts: ToastMessage[] }
    | { type: "item-appear"; item: Item }
    | { type: "educational"; educationalMessage: EducationalMessage };

// -----------------------------------------------------------------------------
// Game State (Persisted)
// -----------------------------------------------------------------------------

export type GameState = {
    version: number;
    gameIsFinished: boolean;
    gameOverReason: GameOverReason;
    currentPhaseId: string | null;
    currentRoomVariant: RoomVariant | null;
    completedPhaseMessages: number;
    nextRoom: Room | null;
    nextRoomVariant: RoomVariant | null;
    stats: Stats;
    inventory: string[]; // Item IDs
    unlockedAchievements: string[]; // Achievement IDs
    completedPhaseIds: string[]; // Track completed phases
    financeTypeId: string | null;
};

// -----------------------------------------------------------------------------
// UI State (Ephemeral - not persisted)
// -----------------------------------------------------------------------------

export type UIState = {
    isPaused: boolean;
    activeOverlay: OverlayType;
    history: HistoryEntry[];

    // --- Pending Response (set on click, consumed on exit complete) ---
    pendingResponse: MessageResponse | null;

    // --- Message Queue Timer ---
    queueTimerActive: boolean;
    queueTimerElapsed: number;
    queueTimerDuration: number;

    // --- Message Queue (ephemeral, reconstructed from phase messages on mount) ---
    /** The message currently being displayed to the player */
    currentMessage: GqlMessage | null;
    /** Upcoming messages (base phase messages + injected follow-ups) */
    messageQueue: GqlMessage[];
    /** Total messages in the current phase (starts with base messages, is increased when optional follow-up message occur) */
    effectivePhaseMessageTotal: number;

    revealedItemId: string | null;

    // --- Post Message Animation Sequencer (ephemeral) ---
    postMessageAnimationQueue: AnimationStep[];
    currentPostMessageAnimationStep: AnimationStep | null;
    /** Follow-up message to inject as currentMessage after animation sequence completes */
    pendingFollowUpMessage: GqlMessage | null;
    /** True when item-appear or educational animation is active (drives dark overlay + HUD hide) */
    darkOverlayVisible: boolean;
};

// -----------------------------------------------------------------------------
// Store Action Types
// -----------------------------------------------------------------------------

export type GameActions = {
    finishGame: () => void;
    // Phase progression
    setCurrentPhaseId: (phaseId: string | null) => void;
    setFinanceTypeId: (financeTypeId: string | null) => void;
    startPhase: (phaseId: string, phaseMessages: GqlMessage[]) => void;
    completePhase: () => void;

    // Room selection
    selectRoom: (variant: RoomVariant) => void;
    clearNextRoom: () => void;

    // Granular stat mutations (called by AnimationLayer during animation steps)
    addCash: (amount: number, historyEntryTitle: string) => void;
    addInvestments: (amount: number, historyEntryTitle: string) => void;
    addHappiness: (amount: number) => void;

    // Inventory
    addItem: (itemId: string) => void;

    // Achievements
    unlockAchievement: (achievementId: string) => void;
    // Reset
    resetGame: () => void;
    setHappiness: (happiness: number) => void;
};

export type UIActions = {
    setOverlay: (overlay: OverlayType) => void;

    // Response flow
    /** Store the clicked response (triggers message exit animation) */
    setPendingResponse: (response: MessageResponse) => void;
    /**
     * Called when message exit animation completes.
     * Evaluates game-over, builds animation steps, and either starts the
     * animation sequence or advances the queue + starts the timer.
     */
    processResponse: () => void;

    /*last revealed item*/
    setRevealedItemId: (id: string) => void;
    clearRevealedItemId: () => void;
    // Queue timer actions
    updateQueueTimerElapsed: (elapsed: number) => void;
    endQueueTimer: () => void;
    startMessageQueue: () => void;
    setQueueTimerDuration: (duration: number) => void;

    // Animation sequencer actions
    /** Complete the current step and advance to the next (or finish the sequence). */
    advancePostMessageAnimationStep: () => void;
};
